import {Injectable} from '@angular/core';
import {CirfConfig} from './cirf-config';
import {Observable} from 'rxjs/index';
import { of } from 'rxjs';
import {connectApi} from '../../common/connect-api';
import {map} from 'rxjs/internal/operators';
import {HttpClient} from '@angular/common/http';
import {CirfField, CirfFieldStatusType, CirfFieldStatusValue} from "./cirf-field";
import {CirfService} from "../cirf.service";
import {SESSION_STORAGE_KEYS} from "../../common/session-storage-keys";
import {Cirf} from "../cirf";
import {CIRF_CONFIG_KEYS} from "./cirf-config-keys";

@Injectable({
    providedIn: 'root'
})
export class CirfConfigService {

    private cachedMatterCirfConfig: Map<number, CirfConfig> = new Map();

    private cachedMatterCirfConfigFieldsStatus: Map<number, Map<string, CirfFieldStatusType>> = new Map();

    private _matterCirfConfig: Map<string, CirfFieldStatusType>;

    private _cirfFields: Map<string, CirfField>;

    constructor(private httpClient: HttpClient) {
    }

    get matterCirfConfig() {
        if (!this._matterCirfConfig) {
            let cirfConfig = this.getCirfConfigFromSessionStorage();
            this.updateMatterCirfConfigFields(cirfConfig);
        }
        return this._matterCirfConfig;
    }

    set matterCirfConfig(value: Map<string, CirfFieldStatusType>) {
        this._matterCirfConfig = value;
    }

    getCirfConfigFromSessionStorage() : CirfConfig {
        let cirfConfigString: string = sessionStorage.getItem(SESSION_STORAGE_KEYS.cirfConfig);
        return cirfConfigString &&  new CirfConfig(JSON.parse(cirfConfigString));
    }

    saveCirfConfigFromSessionStorage(cirfConfig: CirfConfig) : void {
        sessionStorage.setItem(SESSION_STORAGE_KEYS.cirfConfig, JSON.stringify(cirfConfig));
    }

    loadMatterCirfConfig(matterId: number): Observable<CirfConfig> {
        const url = connectApi.getCirfConfig.replace('{matterId}', matterId.toString());
        return this.httpClient
            .get<CirfConfig>(url)
            .pipe(map(result => {
                const newCirfConfig = new CirfConfig(result['CirfConfig']);
                this.cachedMatterCirfConfig.set(matterId, newCirfConfig);
                this.loadFieldsStatus(matterId, newCirfConfig);
                return newCirfConfig;
            }));
    }

    loadFieldsStatus(matterId: number, cirfConfig: CirfConfig): void {
        if (cirfConfig) {
            this.saveCirfConfigFromSessionStorage(cirfConfig);
            this.updateMatterCirfConfigFields(cirfConfig);
            this.cachedMatterCirfConfigFieldsStatus.set(matterId, this.matterCirfConfig);
        }
    }

    updateMatterCirfConfigFields(cirfConfig: CirfConfig) : void {
        this.matterCirfConfig = new Map();
        this._cirfFields = new Map();
        cirfConfig.cirfTopics.forEach(topic =>
            topic.topicSections.forEach(section =>
                section.cirfFields.forEach((field) => {
                    this.matterCirfConfig.set(field.fieldKey, field.fieldStatus);
                    this._cirfFields.set(field.fieldKey, field);
                })));

    }

    getMatterCirfConfig(matterId: number): Observable<CirfConfig> {

        if (this.cachedMatterCirfConfig && this.cachedMatterCirfConfig.has(matterId)) {
            let cachedMatterCirfConfig: CirfConfig = this.cachedMatterCirfConfig.get(matterId);
            this.updateMatterCirfConfigFields(cachedMatterCirfConfig);
            return of(cachedMatterCirfConfig);
        } else {
            return this.loadMatterCirfConfig(matterId);
        }
    }

    clearMatterCirfConfigCache(): void {
        this.cachedMatterCirfConfig = new Map();
        this.matterCirfConfig = new Map();
        this._cirfFields = new Map();
        sessionStorage.removeItem(SESSION_STORAGE_KEYS.cirfConfig);

    }


    /**
     * returns the status of the field
     * @param fieldKey - For example "PERSONAL_DETAILS.CLIENT_PROFILE.FIRST_NAME"
     * @return CirfFieldStatusType
     *
     */
    getFieldStatus(fieldKey: string): CirfFieldStatusType {
        if (this.matterCirfConfig && this.matterCirfConfig.has(fieldKey)) {
            return this.matterCirfConfig.get(fieldKey);
        }
        // the reason the default value for a given key is HIODEN is because some fields (like matterInfo.transactionDetails.Capacity) might show on a Purchase but not on other matters
        // while building a list of available fields based on CIRF_CONFIG_KEYS the key "Capacity" would be available as a key on list of items for the section but there would be no value from CirfConfig for it
        return (fieldKey && fieldKey.length) ? CirfFieldStatusValue.hidden : CirfFieldStatusValue.optional;
    }

    /**
     * checks hidden status for all fields in a section inside CIRF_CONFIG_KEYS
     * @param section - For example cirfClientInfo
     * @return boolean - true if all fields are hidden
     *
     */
    areFieldsForSectionHidden(section: string): boolean {
        const allKeys: string = this.getAllKeysForSection(CIRF_CONFIG_KEYS, section);
        return this.areKeysHidden(allKeys);
    }

    /**
     * checks hidden status for all fields in a combinedNames separated by ';'
     * @param combinedFieldNames - For example 'cirfClientInfo.identification1.identificationTypeCode;cirfClientInfo.identification1.identificationNumber'
     * @return boolean - true if all fields are hidden
     *
     */
    areFieldsHidden(combinedFieldNames: string): boolean {
        const fields: string[] = combinedFieldNames.split(';');
        return fields.every(fieldName => this.isFieldHidden(fieldName));
    }

    isFieldHidden(fieldName: string): boolean {
        const fieldKey: string = this.getFieldKeyFromConfigKey(CIRF_CONFIG_KEYS, fieldName);
        //console.log(`testing isFieldHidden ${fieldName} ${this.getFieldStatus(fieldKey)}`);
        return this.getFieldStatus(fieldKey) === CirfFieldStatusValue.hidden;
    }

    isUDFHidden(fieldKey: string) : boolean {
        return this.isKeyHidden(fieldKey);
    }

    areAllUDFsHidden(fieldKeyPart: string, count: number) : boolean {
        let AllHidden: boolean = true;
        for(let i=0; i<count; i++){
            if(!this.isUDFHidden(fieldKeyPart+i)){
                AllHidden = false;
                break;
            }
        }
        return AllHidden;
    }

    isFieldMandatory(fieldKey: string) : boolean {
        return this.getFieldStatus(fieldKey) == CirfFieldStatusValue.mandatory;
    }

    isFieldByNameMandatory(fieldName: string): boolean {
        const fieldKey: string = this.getFieldKeyFromConfigKey(CIRF_CONFIG_KEYS, fieldName);
        return this.isFieldMandatory(fieldKey);
    }

    private getAllKeysForSection(configKey: any, section: string): string {
        let result = '';
        let values;
        if (section && configKey[section]) {
            values = Object.values(configKey[section]);
        } else if (configKey) {
            values = Object.values(configKey);
        }
        for (let i = 0; i < values.length; i++) {
            if (typeof values[i] === 'object') {
                result += this.getAllKeysForSection(values[i], null) + ((i !== values.length - 1) ? ';' : '');
            } else {
                result += values[i] + ((i !== values.length - 1) ? ';' : '');
            }
        }
        return result;
    }

    private areKeysHidden(keys: string): boolean {
        const fieldConfigKeys: string[] = keys.split(';');
        return fieldConfigKeys.every(key => this.isKeyHidden(key));
    }

    private isKeyHidden(fieldKey: string): boolean {
        return this.getFieldStatus(fieldKey) === CirfFieldStatusValue.hidden;
    }

    private getFieldKeyFromConfigKey(configKey: any , key: string): any {
        if (key.indexOf('.') > -1) {
            const firstConfigKey = key.split('.')[0];
            if (configKey[firstConfigKey]) {
                return this.getFieldKeyFromConfigKey(configKey[firstConfigKey], key.substring(firstConfigKey.length + 1));
            }
            console.log(`${configKey} not found`);
            return ''; // not found
        }
        if (configKey[key]) {
            return configKey[key];
        }
        return configKey;
    }

    getSectionUdfCount(sectionKey: string) : number {
        if(this.matterCirfConfig){
            let matchingKeys = Array.from(this.matterCirfConfig.keys()).filter(key=>key.startsWith(sectionKey));
            return matchingKeys && matchingKeys.length;
        } else {
            return 0;
        }
    }

    getUdfFieldLabel(udfKey: string) : string {
        let udfField = this._cirfFields.get(udfKey);
        return udfField && udfField.fieldLabel;
    }


}
